import { useTranslate } from '@va/localization';
import { ComparisonFilter } from '@va/shared/feature-filters';
import { comparisonOperators, percentComparisonOperators } from '@va/types/filters';
import { useMemo } from 'react';
import { FilterHook } from '../types';

/**
 * These filters should not be applied to the line chart, they match its metrics
 */
export const METRIC_FILTER_MEMBERS: string[] = [
  'sessionCount',
  'visitCount',
  'visitorCount',
  'newVisitorCount',
  'returningVisitorCount',
  'continuationRate',
  'averageSessionDuration',
  'averageVisitDuration',
  'bounceRate',
  'advancedBounceRate',
  'conversionEventCount',
  'pageCount',
];

export const useSessionCountFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'sessionCount',
        name: 'sessionCount',
        label: translate('filter.sessionCount'),
        operatorOptions: comparisonOperators,
        ...params,
        inputProps: { type: 'number', ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useVisitCountFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'visitCount',
        name: 'visitCount',
        label: translate('filter.visitCount'),
        operatorOptions: comparisonOperators,
        ...params,
        inputProps: { type: 'number', ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useVisitorCountFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'visitorCount',
        name: 'visitorCount',
        label: translate('filter.visitorCount'),
        operatorOptions: comparisonOperators,
        ...params,
        inputProps: { type: 'number', ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useNewVisitorCountFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'newVisitorCount',
        name: 'newVisitorCount',
        label: translate('filter.newVisitorCount'),
        operatorOptions: comparisonOperators,
        ...params,
        inputProps: { type: 'number', ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useReturningVisitorCountFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'returningVisitorCount',
        name: 'returningVisitorCount',
        label: translate('filter.returningVisitorCount'),
        operatorOptions: comparisonOperators,
        ...params,
        inputProps: { type: 'number', ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useContinuationRateFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'continuationRate',
        name: 'continuationRate',
        label: translate('filter.continuationRate'),
        operator: percentComparisonOperators[0],
        operatorOptions: percentComparisonOperators,
        ...params,
        inputProps: { type: 'number', label: '%', ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useAverageSessionDurationFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'averageSessionDuration',
        name: 'averageSessionDuration',
        label: translate('filter.averageSessionDuration'),
        operatorOptions: comparisonOperators,
        ...params,
        inputProps: { type: 'number', label: translate('labels.seconds'), ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useAverageVisitDurationFilter: FilterHook<typeof ComparisonFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'averageVisitDuration',
        name: 'averageVisitDuration',
        label: translate('filter.averageVisitDuration'),
        operatorOptions: comparisonOperators,
        ...params,
        inputProps: { type: 'number', label: translate('labels.seconds'), ...params?.inputProps },
      }),
    [params, translate],
  );
};

export const useBounceRateFilter = () => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'bounceRate',
        name: 'bounceRate',
        label: translate('filter.bounceRate'),
        operatorOptions: comparisonOperators,
        inputProps: { type: 'number' },
      }),
    [translate],
  );
};

export const useAdvancedBounceRateFilter = () => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'advancedBounceRate',
        name: 'advancedBounceRate',
        label: translate('filter.advancedBounceRate'),
        operatorOptions: comparisonOperators,
        inputProps: { type: 'number' },
      }),
    [translate],
  );
};

export const useConversionEventCountFilter = () => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'conversionEventCount',
        name: 'conversionEventCount',
        label: translate('filter.conversionEventCount'),
        operatorOptions: comparisonOperators,
        inputProps: { type: 'number' },
      }),
    [translate],
  );
};

export const usePageCountFilter = () => {
  const translate = useTranslate();
  return useMemo(
    () =>
      new ComparisonFilter({
        id: 'pageCount',
        name: 'pageCount',
        label: translate('filter.pageCount'),
        operatorOptions: comparisonOperators,
        inputProps: { type: 'number' },
      }),
    [translate],
  );
};
